import { useState } from 'react';
import { api, type ApplicationCapability, type ProjectApplication, type StoredApplicationCapability } from '../api';
import { useResource } from '../hooks';
import { CatalogSearchSection } from './CatalogSearchSection';
import { Callout, Section } from './primitives';
import { ErrorNotice, LoadingRows } from './ui';

/**
 * CatalogSearchCard shows the catalog search engine the project's Magento
 * code runs on — the verdict discovered from the repository and, once the
 * store runs, confirmed (or contradicted) by it — and lets an operator ask
 * the running store directly. Nothing here changes the server: an
 * inspection reads `bin/magento config:show catalog/search/engine` and the
 * module registrations, then reconciles the environment's Magento setup
 * with what it found.
 */
export function CatalogSearchCard({
  projectId,
  environmentId,
  applications,
  canInspect,
}: {
  projectId: string;
  environmentId: string;
  applications: ProjectApplication[];
  /** Whether the environment's Magento is placed on a server with an agent. */
  canInspect: boolean;
}) {
  const [reloadToken, setReloadToken] = useState(0);
  const stored = useResource(() => api.listProjectCapabilities(projectId), [projectId, reloadToken], undefined, { key: `project:${projectId}:capabilities` });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);

  const magento = applications.find((a) => a.type === 'magento_backend' || a.application_type === 'magento_backend');
  const record: StoredApplicationCapability | undefined = (stored.data?.capabilities ?? []).find(
    (c) => c.key === 'catalog_search' && (!magento || c.application_id === magento.id),
  );
  const capability: ApplicationCapability | null = record?.capability ?? null;

  async function inspect() {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.inspectCatalogSearchRuntime(environmentId);
      const ins = res.inspection;
      const engine = ins.observation.engine_identifier || 'no saved value';
      if (ins.capability.status === 'conflict') {
        setNotice(`The running store reports catalog/search/engine = ${engine}, which disagrees with the repository. Search provisioning is paused until this is reconciled; nothing was changed on the server.`);
      } else if (ins.capability.confidence === 'confirmed') {
        setNotice(`Confirmed: the running store uses ${engine}${ins.capability.module ? ` provided by ${ins.capability.module}` : ''}.`);
      } else {
        setNotice(`The running store reports catalog/search/engine = ${engine}.${ins.configuration_updated ? ' The environment’s Magento setup was updated to match.' : ''}`);
      }
      setReloadToken((v) => v + 1);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      id={`catalog-search-card-${environmentId}`}
      testId="catalog-search-card"
      title="Catalog search"
      actions={
        <button
          type="button"
          className="button button--secondary"
          onClick={inspect}
          disabled={busy || !canInspect}
          data-testid="catalog-search-inspect"
          title={canInspect ? undefined : 'Place the Magento application on a server with an agent first'}
        >
          {busy ? 'Asking the store' : 'Ask the running store'}
        </button>
      }
    >
      {notice ? <Callout tone="info" testId="catalog-search-notice">{notice}</Callout> : null}
      <ErrorNotice error={actionError} />
      {stored.error ? <ErrorNotice error={stored.error} /> : null}
      {stored.loading && !stored.data ? <LoadingRows label="Loading catalog search…" rows={2} rowHeight="44px" /> : null}

      {stored.data && !capability ? (
        <p className="quiet-empty" data-testid="catalog-search-empty">
          Not analysed yet
        </p>
      ) : null}

      {capability ? (
        <>
          <CatalogSearchSection capability={capability} compact />
          {record?.previous && record.changed_at ? (
            <p className="type-meta" data-testid="catalog-search-changed">
              Changed {new Date(record.changed_at).toLocaleString()}: previously {record.previous.provider_name}.
            </p>
          ) : null}
          {capability.runtime?.search_tables && capability.runtime.search_tables.length > 0 ? (
            <details className="quiet-disclosure">
              <summary>Search tables ({capability.runtime.search_tables.length})</summary>
              <p className="type-meta">
                In the Magento database, covered by the regular database backup: <code>{capability.runtime.search_tables.join(', ')}</code>
              </p>
            </details>
          ) : null}
        </>
      ) : null}
    </Section>
  );
}
